/**
 * The mark's two bubbles (UX spec v3 §C.3), drawn by vault.css off `#app[data-hint]`:
 *
 * - "tip" — "Tap to type · swipe up to speak" (DCTA-216 W12), on the first visit to Home, once per device
 *   (localStorage, try/catch: a private window or blocked storage shows it for this
 *   visit and never throws); it goes on the first tap, scroll or key anywhere and never returns. While it shows, Home
 *   keeps a band clear under it (app.css 63, QA D6); a tap hides the tip at once but holds the band until the tap ends
 *   (`data-hint-band="held"`): released on the way down, the page moved under the finger and the tap landed elsewhere.
 * - "off" — "Microphone is off. Tap to type.", after a hold with the microphone off,
 *   for 2.4 s. The status is filled only while shown, so a screen reader hears it.
 */
export const HINT_KEY = "dcta.aim-hint.seen";
const OFF_TEXT = "Microphone is off. Tap to type.";
const OFF_MS = 2400;

interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface Target {
  addEventListener(type: string, fn: EventListener, options?: boolean | AddEventListenerOptions): void;
}

export class AimHints {
  readonly #host: HTMLElement;
  readonly #doc: Target;
  readonly #storage: () => Store;
  readonly #later: (fn: () => void, ms: number) => void;
  /** Seen in this visit (storage may be unavailable). */
  #seen = false;
  #offSeq = 0;
  #listening = false;

  constructor(host: HTMLElement, doc: Target, storage: () => Store, later: (fn: () => void, ms: number) => void) {
    this.#host = host;
    this.#doc = doc;
    this.#storage = storage;
    this.#later = later;
  }

  firstVisit(): void {
    if (this.#seen || this.#stored()) return;
    this.#host.dataset["hint"] = "tip";
    if (this.#listening) return;
    this.#listening = true;
    // The first tap anywhere — and (DCTA-118 P3-3) the first scroll, wheel or key: on a fresh phone the hint sat over a
    // figure until something was tapped. Captured, so a scroll in any scroller (scroll does not bubble) is seen.
    for (const type of ["pointerdown", "wheel", "scroll", "keydown"]) this.#doc.addEventListener(type, () => this.#dismiss(type === "pointerdown"), { capture: true, once: true, passive: true });
  }

  /**
   * DCTA-216 (10-welcome.md §0.2): the first-run welcome closed — it taught the mark, so the one-time tip never follows
   * it (and goes if it is showing). Kept on this device; never throws.
   */
  seen(): void {
    this.#seen = true;
    if (this.#host.dataset["hint"] === "tip") delete this.#host.dataset["hint"];
    try {
      this.#storage().setItem(HINT_KEY, "1");
    } catch {
      // Not persisted: gone for this visit.
    }
  }

  voiceOff(): void {
    const seq = ++this.#offSeq;
    const off = this.#host.querySelector(".aim-hint.off");
    if (off !== null) off.textContent = OFF_TEXT;
    this.#host.dataset["hint"] = "off";
    this.#later(() => {
      if (seq !== this.#offSeq || this.#host.dataset["hint"] !== "off") return;
      delete this.#host.dataset["hint"];
      this.#host.querySelectorAll(".aim-hint.off").forEach((n) => (n.textContent = ""));
    }, OFF_MS);
  }

  /** `tap`: dismissed by a finger (or a mouse) going down — the band waits for it to come up. */
  #dismiss(tap: boolean): void {
    this.#seen = true;
    if (this.#host.dataset["hint"] === "tip") {
      delete this.#host.dataset["hint"];
      if (tap) this.#holdBand();
    }
    try {
      this.#storage().setItem(HINT_KEY, "1");
    } catch {
      // Not persisted: it stays gone for this visit.
    }
  }

  #holdBand(): void {
    this.#host.dataset["hintBand"] = "held";
    const release = (): void => void delete this.#host.dataset["hintBand"];
    for (const type of ["pointerup", "pointercancel"]) this.#doc.addEventListener(type, release, { capture: true, once: true, passive: true });
  }

  #stored(): boolean {
    try {
      return this.#storage().getItem(HINT_KEY) === "1";
    } catch {
      return false;
    }
  }
}
